import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useBusinessStore from '../state/businessStore';
import { updateUserProfile } from '../api/user';
import { useAuthStorage } from '../hooks/useAuthStorage';
import {
  TIMEZONES,
  getTimezone,
  gmtOffset,
  setBusinessTimezone,
  setUserTimezone,
} from '../utils/timezone';

import './TimezoneClock.scss';

const MIN_CHARS = 3;

const normalize = (value) => value.toLowerCase().replace(/[_\s]+/g, ' ');

const TimezoneClock = () => {
  const { t, i18n } = useTranslation('common');
  const { getToken } = useAuthStorage();
  const businessTz = useBusinessStore((s) => s.getSelectedBusiness()?.timezone);
  const [now, setNow] = useState(() => new Date());
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (businessTz) setBusinessTimezone(businessTz);
  }, [businessTz]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const results = useMemo(() => {
    if (query.trim().length < MIN_CHARS) return [];
    const q = normalize(query.trim());
    return TIMEZONES.filter((tz) => normalize(tz).includes(q)).slice(0, 50);
  }, [query]);

  const timezone = getTimezone();

  const close = () => {
    setOpen(false);
    setQuery('');
    setActive(0);
  };

  const select = async (tz) => {
    close();
    if (tz === timezone) return;
    setUserTimezone(tz);
    try {
      await updateUserProfile(getToken(), { timezone: tz });
    } catch (error) {
      console.error('Error saving timezone:', error);
    }
    window.location.reload();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') close();
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[active]);
    }
  };

  const lang = i18n.language;

  return (
    <div className="tz-clock">
      <div className="tz-clock__now">
        <span className="tz-clock__time">
          {now.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}
        </span>
        <span className="tz-clock__date">
          {now.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' })}
        </span>
      </div>

      <div className="tz-clock__picker">
        {open ? (
          <input
            ref={inputRef}
            className="tz-clock__input"
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls="tz-clock-list"
            aria-label={t('timezone.label')}
            placeholder={t('timezone.search_placeholder')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            onBlur={close}
          />
        ) : (
          <button
            type="button"
            className="tz-clock__button"
            title={t('timezone.label')}
            onClick={() => setOpen(true)}
          >
            <span className="tz-clock__zone">{timezone.replace(/_/g, ' ')}</span>
            <span className="tz-clock__offset">{gmtOffset(timezone, now)}</span>
          </button>
        )}

        {open && (
          <ul id="tz-clock-list" className="tz-clock__list" role="listbox">
            {query.trim().length < MIN_CHARS ? (
              <li className="tz-clock__hint">{t('timezone.min_chars', { count: MIN_CHARS })}</li>
            ) : results.length === 0 ? (
              <li className="tz-clock__hint">{t('timezone.no_results')}</li>
            ) : (
              results.map((tz, i) => (
                <li
                  key={tz}
                  role="option"
                  aria-selected={i === active}
                  className={`tz-clock__option ${i === active ? 'is-active' : ''} ${tz === timezone ? 'is-current' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(tz);
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  <span>{tz.replace(/_/g, ' ')}</span>
                  <span className="tz-clock__offset">{gmtOffset(tz, now)}</span>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TimezoneClock;
